import { useEffect, useState } from "react";
import { SmartTable, SmartTableNewInterface } from "soft_digi";
import { PAYMENT_URLS } from "../../api/UserUrls";
import { sumOfArrayObjectsWithIndex } from "../../services/core/FilterService";
import { post } from "../../services/smartApiService";
import { formatCurrency } from "../../services/core/CommonService";
import ExcelJS from 'exceljs';

interface Props {
  sd_customer_id: any;
}

const CustomerLedger: React.FC<Props> = ({ sd_customer_id }) => {
  const [data, setData] = useState<any>({});

  const loadData = () => {
    let _data = {
      sd_customer_id: sd_customer_id,
    };
    let URL = PAYMENT_URLS.GET_CUSTOMER_LEDGER;
    const subscription = post(URL, _data).subscribe((response) => {
      setData(response.data);
    });
    return () => {
      subscription.unsubscribe();
    };
  };

  const exportToExcel = async () => {
    try {
      if (!data?.data) {
        throw new Error("No data available to export");
      }

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Customer Ledger');
      
      // Add title
      worksheet.mergeCells('A1:F1');
      const titleCell = worksheet.getCell('A1');
      titleCell.value = 'Customer Ledger Report';
      titleCell.font = { bold: true, size: 16 };
      titleCell.alignment = { horizontal: 'center' };
      titleCell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFFF00' }
      };

      // Add summary report
      const invoice_amount = sumOfArrayObjectsWithIndex(data?.invoice_data, "amount");
      const paid_amount = sumOfArrayObjectsWithIndex(data?.payment_data, "amount");
      const rem_amount = invoice_amount - paid_amount;

      worksheet.addRow([]); // Empty row
      
      const summaryTitle = worksheet.addRow(['Summary Report']);
      summaryTitle.font = { bold: true, color: { argb: 'FF0000FF' } };
      
      const summaryHeaders = worksheet.addRow([
        'Total Invoice Amount', 
        'Paid Amount', 
        'Pending Amount'
      ]);
      summaryHeaders.eachCell((cell) => {
        cell.font = { bold: true };
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFD3D3D3' }
        };
      });

      const summaryValues = worksheet.addRow([
        formatCurrency(invoice_amount),
        formatCurrency(paid_amount),
        formatCurrency(rem_amount)
      ]);
      
      // Style summary values
      summaryValues.getCell(1).font = { color: { argb: 'FF0000FF' } };
      summaryValues.getCell(2).font = { color: { argb: 'FF008000' } };
      summaryValues.getCell(3).font = { color: { argb: 'FFFF0000' } };

      worksheet.addRow([]); // Empty row

      // Add ledger table headers
      const headers = [
        'S.No', 
        'Date', 
        'Invoice Number/Payment Mode', 
        'Invoice Amount', 
        'Paid Amount'
      ];
      const headerRow = worksheet.addRow(headers);
      
      // Style headers
      headerRow.eachCell((cell) => {
        cell.font = { bold: true };
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFD3D3D3' }
        };
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' }
        };
      });

      // Add ledger data
      data.data.forEach((item: any, index: number) => {
        const rowData = [
          index + 1,
          item.date,
          item.ref_no,
          item.status === "1" ? formatCurrency(item.amount) : "",
          item.status === "2" ? formatCurrency(item.amount) : ""
        ];
        
        const row = worksheet.addRow(rowData);
        
        // Style rows
        row.eachCell((cell) => {
          cell.border = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' }
          };
        });
        
        // Color coding
        if (item.status === "1") {
          row.getCell(4).font = { color: { argb: 'FF008000' } };
        } else if (item.status === "2") {
          row.getCell(5).font = { color: { argb: 'FFFF0000' } };
        }
      });

      // Auto-size columns with proper null checks
      worksheet.columns?.forEach((column) => {
        if (!column) return;

        let maxLength = 0;
        column.eachCell?.({ includeEmpty: true }, (cell) => {
          const columnLength = cell.value ? cell.value.toString().length : 0;
          if (columnLength > maxLength) {
            maxLength = columnLength;
          }
        });

        column.width = Math.min(Math.max(maxLength + 2, 10), 50);
      });

      // Generate and download the file
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Customer_Ledger_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      // Add your error notification here
    }
  };

  useEffect(() => {
    loadData();
  }, [sd_customer_id]);

  const columns: SmartTableNewInterface.SmartTableNewColumnConfig[] = [
    { title: "S.NO", index: "s_no", type: "sno", width: "5" },
    {
      title: "Date",
      index: "date",
      type: "date"
    },
    {
      title: "Invoice Number/Payment Mode",
      index: "ref_no",
    },    
    {
      title: "Invoice Amount",
      index: "invoice_amount",
      classBody: "has-text-success",
      valueFunction: (data) => {
        return data["status"] === "1" ? formatCurrency(data["amount"]) : "";
      }
    },
    {
      title: "Paid",
      index: "amount",
      classBody: "has-text-danger",
      valueFunction: (data) => {
        return data["status"] === "2" ? formatCurrency(data["amount"]) : "";
      }
    },
  ];

  const tableTop: SmartTableNewInterface.SmartTableNewTopProps[] = [
    {
      type: "CUSTOM",
      widthClass: "is-6",
      custom: (
        <p className="is-size-4">
          <i className="fa fa-file-text is-link"></i>
          <span className="ml-3"> Customer Ledger</span>
        </p>
      ),
    },   
    {
      type: "CUSTOM",
      widthClass: "is-6",
      custom: (
        <div className="is-flex is-justify-content-flex-end">
          <button 
            className="button is-primary is-small"
            onClick={exportToExcel}
            disabled={!data?.data}
          >
            <span className="icon">
              <i className="fas fa-file-export"></i>
            </span>
            <span>Export to Excel</span>
          </button>
        </div>
      ),
    }, 
  ];

  const summaryReport = () => {
    if (!data?.invoice_data || !data?.payment_data) return null;
    
    const invoice_amount = sumOfArrayObjectsWithIndex(data.invoice_data, "amount");
    const paid_amount = sumOfArrayObjectsWithIndex(data.payment_data, "amount");
    const rem_amount = invoice_amount - paid_amount;
    
    return (   
      <div>
        <p className="has-text-link"><b>Summary Report</b></p>
        <table className="table is-fullwidth is-bordered is-hoverable is-striped smart-efl-table">
          <thead>
            <tr>
              <th>Total Invoice Amount</th>
              <th>Paid Amount</th>
              <th>Pending Amount</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="has-text-primary has-text-weight-bold">{formatCurrency(invoice_amount)}</td>
              <td className="has-text-success has-text-weight-bold">{formatCurrency(paid_amount)}</td>
              <td className="has-text-danger has-text-weight-bold">{formatCurrency(rem_amount)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="">
      {data && data.data && 
        <>
          {summaryReport()}
          <br/>
          <div className="smart-elf-table">
            <SmartTable
              columns={columns}
              data={data.data}
              tableTop={tableTop}
              tableProps={{
                className: "is-hoverable is-bordered is-striped smart-efl-table",
                isResponsive: true,
                searchPlaceHolder: "Search",
              }}
              paginationProps={{ pageSize: 10 }}
            />
          </div>
        </>
      }
    </div>
  );
};

export default CustomerLedger;